import { useState, useEffect, useRef } from "react";
import InputField from "components/fields/InputField";
import Default from "layouts/auth/types/Default";
import Checkbox from "components/checkbox";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import DropdownForForm from "components/dropdown/dropdownForForm";
import { debounce } from "lodash";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (() => {
  throw new Error("API_BASE_URL is not defined");
})();

function SignUpDefault() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phoneNumber: "",
    countryCode: "+254",
    school: "",
    password: "",
    confirmPassword: "",
    country: "Kenya",
  });
  const [errors, setErrors] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [checking, setChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const cancelRef = useRef(null);

  // Clean input for username generation
  const clean = (s = "") =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .trim();

  // Generate username candidates
  const buildCandidates = (first, last) => {
    const f = clean(first);
    const l = clean(last);
    if (!f && !l) return [];
    const base = [];
    if (f && l) {
      base.push(`${f}${l[0]}`); // johns
      base.push(`${f[0]}${l}`); // jsmith
    }
    if (f && f.length >= 5) base.push(f.slice(0, 13)); // john
    if (l && l.length >= 5) base.push(l.slice(0, 13)); // smith
    return base.map((u) => u.slice(0, 13)).filter((u) => u.length >= 5);
  };

  const countryCodeOptions = [
    { value: "+254", label: "+254 (Kenya)" },
    { value: "+252", label: "+252 (Somalia)" },
  ];

  // Handle input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((p) => ({ ...p, [id]: value }));
    setErrors((p) => ({ ...p, [id]: "", general: "" }));
  };

  const handleCountryCodeSelect = (value) => {
    setFormData((p) => ({ ...p, countryCode: value }));
    setErrors((p) => ({ ...p, phoneNumber: "", general: "" }));
  };

  const toggleShowPassword = () => setShowPassword((p) => !p);
  const handleCheckboxChange = () => setIsChecked((p) => !p);

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    return score;
  };

  // Handle password change with strength checking
  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, password: value }));
    setPasswordStrength(checkPasswordStrength(value));
    setErrors(prev => ({ ...prev, password: "", confirmPassword: "" }));
  };

  // Auto-set country based on school
  useEffect(() => {
    if (!formData.school) return;
    const somalia = ["SIMAD University", "iForge Somalia"];
    const country = somalia.includes(formData.school) ? "Somalia" : "Kenya";
    setFormData((p) => ({
      ...p,
      country,
      countryCode: country === "Somalia" ? "+252" : "+254",
    }));
  }, [formData.school]);

  // Generate username suggestions (debounced)
  const checkUsernames = debounce(async (wanted) => {
    if (!wanted.length) {
      setSuggestions([]);
      return;
    }

    try {
      cancelRef.current?.cancel?.();
      cancelRef.current = axios.CancelToken.source();
      setChecking(true);

      const found = [];
      let tries = 0;

      for (const candidate of wanted) {
        if (found.length >= 3) break;
        const { data } = await axios.get(`${API_BASE_URL}/api/auth/check-username`, {
          params: { username: candidate },
          cancelToken: cancelRef.current.token,
          timeout: 5000,
        });
        if (data.available && !found.includes(candidate)) found.push(candidate);
      }

      while (found.length < 3 && tries < 30) {
        const base = wanted[0] || clean(formData.firstName + formData.lastName);
        const extra = `${base}${Math.floor(10 + Math.random() * 90)}`.slice(0, 13);
        tries += 1;
        if (extra.length < 5) continue;
        const { data } = await axios.get(`${API_BASE_URL}/api/auth/check-username`, {
          params: { username: extra },
          cancelToken: cancelRef.current.token,
          timeout: 5000,
        });
        if (data.available && !found.includes(extra)) found.push(extra);
      }

      setSuggestions(found.slice(0, 3));
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Username check failed:", error);
        setSuggestions(wanted.slice(0, 3)); // Fallback
      }
    } finally {
      setChecking(false);
    }
  }, 500);

  useEffect(() => {
    const wanted = buildCandidates(formData.firstName, formData.lastName);
    checkUsernames(wanted);
  }, [formData.firstName, formData.lastName]);

  useEffect(() => () => checkUsernames.cancel(), []);

  const selectSuggestion = (s) => {
    setFormData((p) => ({ ...p, username: s }));
    setErrors((p) => ({ ...p, username: "", general: "" }));
  };

  // Validation
  const validate = () => {
    const e = {};
    const f = formData;
    const nameR = /^[A-Za-z\s\-'\.]+$/;
    const mailR = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneR = /^\+?[1-9]\d{9,14}$/;
    const sanitizedPhone = formData.phoneNumber.trim().replace(/^0+/, '');
    const fullPhone = `${formData.countryCode}${sanitizedPhone}`;


    if (!f.firstName.trim()) e.firstName = "First name is required";
    else if (!nameR.test(f.firstName)) e.firstName = "Use letters, spaces, hyphens, apostrophes, or periods";
    if (!f.lastName.trim()) e.lastName = "Last name is required";
    else if (!nameR.test(f.lastName)) e.lastName = "Use letters, spaces, hyphens, apostrophes, or periods";
    if (!f.username.trim()) e.username = "Username is required";
    else if (!/^[a-zA-Z0-9]{5,13}$/.test(f.username)) e.username = "Username must be 5-13 characters, letters and numbers only";
    if (!f.email.trim()) e.email = "Email is required";
    else if (!mailR.test(f.email)) e.email = "Invalid email address";
    if (!f.phoneNumber.trim()) e.phoneNumber = "Phone number is required";
    else if (!phoneR.test(fullPhone)) e.phoneNumber = "Phone number must be in international format (e.g., +2547XXXXXXXX)";
    if (!f.school) e.school = "Institution is required";
    if (!f.country) e.country = "Country is required";
    if (!f.password) e.password = "Password is required";
    else if (f.password.length < 8) e.password = "Password must be at least 8 characters";
    if (!f.confirmPassword) e.confirmPassword = "Please confirm your password";
    else if (f.password !== f.confirmPassword) e.confirmPassword = "Passwords do not match";
    if (!isChecked) e.agreement = "You must accept the Terms & Conditions";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSignUp = async () => {
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const fullPhone = `${formData.countryCode}${formData.phoneNumber.trim()}`;
      await axios.post(
        `${API_BASE_URL}/api/auth/register`,
        {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          username: formData.username.trim(),
          email: formData.email.trim(),
          phoneNumber: fullPhone,
          school: formData.school,
          password: formData.password,
          country: formData.country,
        },
        { timeout: 10000 }
      );
      localStorage.setItem('registrationEmail', formData.email.trim().toLowerCase());
      setIsSuccess(true);
      setTimeout(() => {
        navigate("/auth/verification");
      }, 2000);
    } catch (error) {
      setIsSubmitting(false);
      if (axios.isAxiosError(error)) {
        const msg = error.response?.data?.msg;
        if (msg) {
          if (msg.includes("Email already in use")) {
            setErrors({ email: "This email is already registered." });
          } else if (msg.includes("Username already in use")) {
            setErrors({ username: "This username is taken. Try another or choose a suggestion." });
          } else if (msg.includes("Phone number already in use")) {
            setErrors({ phoneNumber: "This phone number is already registered." });
          } else if (msg.includes("failed to send verification email")) {
            setErrors({
              general:
                "Registration successful, but verification email failed. Please resend from the verification page.",
            });
          } else if (msg.includes("Username must be")) {
            setErrors({ username: "Username must be 5-13 characters, letters and numbers only" });
          } else {
            setErrors({ general: msg || "Signup failed. Please try again." });
          }
        } else {
          setErrors({ general: "Network error. Please check your connection and try again." });
        }
      } else {
        setErrors({ general: "An unexpected error occurred. Please try again." });
      }
    }
  };

  return (
    <Default
      maincard={
        <div className="mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-start lg:justify-start">
          <div className="mt-[3vh] w-full max-w-full flex-col md:pl-4 lg:pl-0 xl:max-w-[420px]">
            <h3 className="text-4xl font-bold text-navy-700 dark:text-white">Sign Up</h3>
            <p className="mt-4 ml-1 text-base text-gray-600 dark:text-gray-300">
              Enter your details to sign up!
            </p>

            {errors.general && (
              <div className="mt-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400 text-sm flex items-center">
                  <span className="mr-2">⚠️</span>
                  {errors.general}
                </p>
              </div>
            )}

            {isSuccess && (
              <div className="mt-6 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-green-600 dark:text-green-400 text-sm flex items-center">
                  <span className="mr-2">✅</span>
                  Account created successfully! Redirecting to verification...
                </p>
              </div>
            )}

            <div className="mt-8 space-y-6">
              <div className="flex w-full items-center gap-4">
                <div className="w-1/2">
                  <InputField
                    variant="auth"
                    label="First Name*"
                    placeholder="John Adam"
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    state={errors.firstName ? "error" : ""}
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div className="w-1/2">
                  <InputField
                    variant="auth"
                    label="Last Name*"
                    placeholder="Smith O'Connor"
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    state={errors.lastName ? "error" : ""}
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <InputField
                  variant="auth"
                  label="Username*"
                  placeholder="JohnSmith"
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  state={errors.username ? "error" : ""}
                />
                {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                {!formData.username && suggestions.length > 0 && (
                  <div className="text-xs text-gray-500 mt-2">
                    {checking ? "Finding usernames…" : "Suggestions: "}
                    {!checking &&
                      suggestions.map((s, i) => (
                        <button
                          key={s}
                          onClick={() => selectSuggestion(s)}
                          type="button"
                          className="underline text-orange-500 mr-2 hover:text-orange-600"
                        >
                          {s}
                          {i < suggestions.length - 1 && ","}
                        </button>
                      ))}
                  </div>
                )}
              </div>

              <div>
                <InputField
                  variant="auth"
                  label="Email*"
                  placeholder="mail@example.com"
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  state={errors.email ? "error" : ""}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div className="flex w-full items-center gap-4">
                <div className="w-1/3">
                  <DropdownForForm
                    label="Country Code"
                    options={countryCodeOptions.map((o) => o.label)}
                    selectedOption={
                      countryCodeOptions.find((o) => o.value === formData.countryCode)?.label ||
                      "+254 (Kenya)"
                    }
                    onSelect={(label) => {
                      const opt = countryCodeOptions.find((o) => o.label === label);
                      handleCountryCodeSelect(opt.value);
                    }}
                    variant="auth"
                    state={errors.phoneNumber ? "error" : ""}
                  />
                </div>
                <div className="w-2/3">
                  <InputField
                    variant="auth"
                    label="Phone Number*"
                    placeholder={formData.countryCode === "+254" ? "712345678" : "612345678"}
                    id="phoneNumber"
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    state={errors.phoneNumber ? "error" : ""}
                  />
                </div>
              </div>
              {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}

              <div>
                <DropdownForForm
                  label="Institution*"
                  options={[
                    "SIMAD University",
                    "Tabsera Academy",
                    "Itqan Academy",
                    "iForge Kenya",
                    "iForge Somalia",
                  ]}
                  selectedOption={formData.school}
                  onSelect={(v) => {
                    setFormData((p) => ({ ...p, school: v }));
                    setErrors((p) => ({ ...p, school: "", general: "" }));
                  }}
                  variant="auth"
                  state={errors.school ? "error" : ""}
                />
                {errors.school && <p className="text-red-500 text-xs mt-1">{errors.school}</p>}
              </div>

              <div>
                <DropdownForForm
                  label="Country*"
                  options={["Kenya", "Somalia"]}
                  selectedOption={formData.country}
                  onSelect={(v) => {
                    setFormData((p) => ({
                      ...p,
                      country: v,
                      countryCode: v === "Somalia" ? "+252" : "+254",
                    }));
                    setErrors((p) => ({ ...p, country: "", general: "" }));
                  }}
                  variant="default"
                  state={errors.country ? "error" : ""}
                />
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>

              <div>
                <InputField
                  variant="auth"
                  label="Password*"
                  placeholder="Min. 8 characters"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handlePasswordChange}
                  state={errors.password ? "error" : ""}
                  icon={showPassword ? <FaEye /> : <FaEyeSlash />}
                  onIconClick={toggleShowPassword}
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}

                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
                      <span>Password strength:</span>
                      <span className={`font-medium ${
                        passwordStrength <= 2 ? 'text-red-500' : 
                        passwordStrength <= 3 ? 'text-yellow-500' : 
                        passwordStrength <= 4 ? 'text-blue-500' : 'text-green-500'
                      }`}>
                        {passwordStrength <= 2 ? 'Weak' : 
                         passwordStrength <= 3 ? 'Fair' : 
                         passwordStrength <= 4 ? 'Good' : 'Strong'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          passwordStrength <= 2 ? 'bg-red-500' : 
                          passwordStrength <= 3 ? 'bg-yellow-500' : 
                          passwordStrength <= 4 ? 'bg-blue-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Password Requirements */}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowPasswordRequirements(!showPasswordRequirements)}
                    className="text-xs text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 underline"
                  >
                    {showPasswordRequirements ? 'Hide' : 'Show'} password requirements
                  </button>
                  {showPasswordRequirements && (
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-navy-800 rounded-lg text-xs text-gray-600 dark:text-gray-400">
                      <p className="font-medium mb-2">Password must contain:</p>
                      <ul className="space-y-1">
                        <li className={`flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{formData.password.length >= 8 ? '✓' : '○'}</span>
                          At least 8 characters
                        </li>
                        <li className={`flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[a-z]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one lowercase letter
                        </li>
                        <li className={`flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one uppercase letter
                        </li>
                        <li className={`flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one number
                        </li>
                        <li className={`flex items-center ${/[^A-Za-z0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                          <span className="mr-2">{/[^A-Za-z0-9]/.test(formData.password) ? '✓' : '○'}</span>
                          At least one special character
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <InputField
                  variant="auth"
                  label="Confirm Password*"
                  placeholder="Re-enter password"
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  state={errors.confirmPassword ? "error" : ""}
                  icon={showPassword ? <FaEye /> : <FaEyeSlash />}
                  onIconClick={toggleShowPassword}
                />
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>

              <div className="flex items-center px-2">
                <Checkbox
                  id="checkbox"
                  color="orange"
                  onChange={handleCheckboxChange}
                  checked={isChecked}
                  aria-label="Accept Terms and Conditions"
                />
                <label
                  htmlFor="checkbox"
                  className="ml-2 text-sm text-navy-700 hover:cursor-pointer dark:text-white"
                >
                  By creating an account, you agree to the{" "}
                  <Link to="/auth/terms-of-service" className="text-orange-400 hover:text-orange-600">
                    Terms and Conditions
                  </Link>{" "}
                  and our{" "}
                  <Link to="/auth/privacy-policy" className="text-orange-400 hover:text-orange-600">
                    Privacy Policy
                  </Link>.
                </label>
              </div>
              {errors.agreement && <p className="text-red-500 text-xs mt-1">{errors.agreement}</p>}

              <button
                className={`w-full rounded-xl py-3 text-base font-medium text-white transition duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] ${
                  isChecked &&
                  Object.entries(formData).every(([k, v]) => (k === "phoneNumber" ? v.trim() : v)) &&
                  passwordStrength >= 3
                    ? "bg-orange-500 hover:bg-orange-600 active:bg-orange-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
                onClick={handleSignUp}
                disabled={
                  isSubmitting ||
                  !isChecked ||
                  !Object.entries(formData).every(([k, v]) => (k === "phoneNumber" ? v.trim() : v)) ||
                  passwordStrength < 3
                }
                aria-label="Create my account"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating Account...
                  </div>
                ) : (
                  "Create my account"
                )}
              </button>

              <div className="text-center">
                <span className="text-sm font-medium text-navy-700 dark:text-gray-500">
                  Already a member?
                </span>
                <Link
                  to="/auth/sign-in"
                  className="ml-1 text-sm font-medium text-orange-400 hover:text-orange-600 dark:text-white"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );

}


export default SignUpDefault;
